import React, { useEffect, useRef, useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import MeltingConsentDocument from './MeltingConsentDocument';
import './MeltingConsentForm.css';

/**
 * Download button for Form AGC/01 — the melting & purity-testing consent the
 * customer signs *before* the jewellery goes into the furnace.
 *
 * It reads the case form's live state rather than a saved record, because the
 * consent is taken at the counter while the case is still being keyed in. On a
 * saved case the Transaction / Receipt No. is the Case ID; on an unsaved one
 * that line prints blank for the branch to fill in.
 */

// Only used when the case has no items yet, so the sheet can still be filled in by hand.
const BLANK_ROWS = 3;
const WEBSITE = 'www.auriksha.com';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtWeight = (v) => (num(v) > 0 ? num(v).toFixed(4) : '');

const maskAadhaar = (a) => {
  const digits = String(a || '').replace(/\D/g, '');
  return digits.length >= 4 ? `XXXX XXXX ${digits.slice(-4)}` : '';
};

/** Aadhaar first (masked), PAN as the fallback — mirrors the agreement PDFs. */
const resolveIdProof = (customer) => {
  if (customer?.aadhaar) return { idProofType: 'Aadhaar', idProofNo: maskAadhaar(customer.aadhaar) };
  if (customer?.pan) return { idProofType: 'PAN', idProofNo: String(customer.pan).toUpperCase() };
  return { idProofType: '', idProofNo: '' };
};

/** `user.branch` is an id straight after login and a populated doc after a profile refresh. */
const resolveBranchName = (user) =>
  (typeof user?.branch === 'object' ? user?.branch?.name : null) || user?.branchName || '';

/**
 * Maps the case form's live state onto the printed form.
 * Exported so the same mapping can be reused elsewhere (e.g. a case detail page).
 */
export const buildConsentData = ({ metalType, customer, user, caseDate, goldItems = [], photos = [] }) => {
  const isSilver = metalType === 'SILVER';
  const when = caseDate ? dayjs(caseDate) : dayjs();

  const rows = goldItems
    .filter((g) => g && (g.itemName || num(g.grossWeight) > 0))
    .map((g, idx) => {
      // The form's "Non-Gold Weight" is everything the melt will not yield as
      // metal: stones/wax plus the dust allowance.
      const nonMetal = num(g.stoneWaxDeduction ?? g.deduction) + num(g.dustDeduction);
      const purityName = g.purityName || '';
      const purityPct = num(g.purity);
      return {
        sl: idx + 1,
        description: g.itemName || '',
        grossWeight: fmtWeight(g.grossWeight),
        nonMetalWeight: fmtWeight(nonMetal),
        netWeight: fmtWeight(g.netWeight),
        // Net weight is purity-adjusted, so the assumed purity has to be on the
        // signed page for the three weight columns to reconcile.
        remarks: [purityName, purityPct ? `${purityPct}%` : ''].filter(Boolean).join(' · '),
      };
    });

  // One printed row per item — the table grows with the case. A case with
  // nothing keyed in yet still prints ruled rows to write on.
  if (rows.length === 0) {
    for (let i = 0; i < BLANK_ROWS; i += 1) {
      rows.push({
        sl: i + 1,
        description: '', grossWeight: '', nonMetalWeight: '', netWeight: '', remarks: '',
      });
    }
  }

  const totalGross = goldItems.reduce((s, g) => s + num(g?.grossWeight), 0);
  const totalNet = goldItems.reduce((s, g) => s + num(g?.netWeight), 0);

  return {
    // Left blank deliberately — the branch writes the form number in by hand.
    formNo: '',
    dateStr: when.format('DD/MM/YYYY'),
    timeStr: when.format('hh:mm A'),
    metalLabel: isSilver ? 'Silver' : 'Gold',
    metalLower: isSilver ? 'silver' : 'gold',
    customer: {
      name: customer?.name || '',
      mobile: customer?.phone || '',
      address: customer?.address || '',
      ...resolveIdProof(customer),
    },
    branchName: resolveBranchName(user),
    employeeName: user?.name || '',
    // Only present once the profile has been refreshed — login alone doesn't
    // return it, in which case the line prints blank to be filled in by hand.
    employeeCode: user?.employeeCode || '',
    items: rows,
    totals: { gross: fmtWeight(totalGross), net: fmtWeight(totalNet) },
    photos,
    website: WEBSITE,
  };
};

const MAX_PHOTOS = 4;

/**
 * Inline the uploaded gold images as data URLs.
 *
 * The stored images live on the CDN, and html2canvas would either taint the
 * canvas or draw an empty box if a fetch is refused — so anything that cannot
 * be inlined is dropped rather than printed blank. Freshly captured images are
 * still local blob: URLs, which inline the same way.
 */
const inlinePhotos = async (goldImages = []) => {
  const sources = goldImages
    .map((img) => img?.src || img?.thumbnailUrl || img?.url)
    .filter(Boolean)
    .slice(0, MAX_PHOTOS);

  const inlined = await Promise.all(
    sources.map(async (src) => {
      if (src.startsWith('data:')) return src;
      try {
        const res = await fetch(src, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch (err) {
        console.warn('Consent form: could not inline gold image', src, err);
        return null;
      }
    }),
  );

  return inlined.filter(Boolean);
};

/** html2canvas paints whatever is decoded at that instant — wait the logos out. */
const waitForImages = (root) =>
  Promise.all(
    Array.from(root.querySelectorAll('img')).map((img) =>
      img.complete && img.naturalWidth
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          }),
    ),
  );

const MeltingConsentButton = ({
  metalType,
  customer,
  user,
  caseCode,
  caseDate,
  goldItems,
  goldImages,
  size = 'small',
  block = false,
  label = 'Melting Consent Form',
}) => {
  const [staged, setStaged] = useState(null);
  const [busy, setBusy] = useState(false);
  const docRef = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();

  const handleClick = async () => {
    setBusy(true);
    const photos = await inlinePhotos(goldImages);
    setStaged(buildConsentData({ metalType, customer, user, caseDate, goldItems, photos }));
  };

  // The document is only mounted while generating, so the case form isn't
  // carrying a second full-page DOM tree on every keystroke.
  useEffect(() => {
    if (!staged) return undefined;
    let cancelled = false;

    const generate = async () => {
      try {
        const el = docRef.current;
        if (!el) throw new Error('Consent form failed to render');
        await waitForImages(el);
        const html2pdf = (await import('html2pdf.js')).default;
        await html2pdf()
          .set({
            margin: 0,
            filename: `Melting-Consent-Form_${caseCode || dayjs().format('YYYYMMDD-HHmm')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 794 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['css', 'legacy'] },
          })
          .from(el)
          .save();
      } catch (err) {
        console.error('Melting consent PDF failed:', err);
        messageApi.error('Could not generate the consent form');
      } finally {
        if (!cancelled) {
          setBusy(false);
          setStaged(null);
        }
      }
    };

    // Give the browser one paint so layout is settled before rasterising.
    const timer = setTimeout(generate, 60);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staged]);

  return (
    <>
      {contextHolder}
      <Tooltip title="Download the melting & purity-testing consent form (AGC/01) for the customer to sign">
        <Button size={size} block={block} icon={<FilePdfOutlined />} loading={busy} onClick={handleClick}>
          {label}
        </Button>
      </Tooltip>
      {staged && (
        <div className="mcf-stage" aria-hidden="true">
          <MeltingConsentDocument ref={docRef} data={staged} />
        </div>
      )}
    </>
  );
};

export default MeltingConsentButton;
